import { useNavigate } from "react-router-dom";
import GridView from "../../components/controls/GridView/GridView";
import { Charge } from "../../models/interfaces";
import chargeService from "../../services/chargeService";
import { useEffect, useState } from "react";
import FormModal from "../../components/controls/FormModal/FormModal";
import { AccountCacheService } from "../../services/accountService";
import FormCommand from "../../components/controls/FormCommand";
import SearchBar from "../../components/controls/SearchBar/SearchBar";
import FindAccount from "../../components/controls/FindAccount/FindAccount";
import Frame from "../../components/controls/Frame/Frame";
import ContextMenu from "../../components/controls/ContextMenu/ContextMenu";
import { MenuItem } from "../../models/menu";
import { MdDelete, MdEdit } from "react-icons/md";

export type ChargeListingPageProp = {
  isModal?: boolean;
};

export function getChargesContextMenu(): MenuItem<Charge>[] {
  return [
    {
      name: "Edit",
      onClick: (charge) => {
        alert(charge.name);
      },
      icon: <MdEdit />,
    },
    {
      name: "Delete",
      onClick: (charge) => {
        alert("Delete charge" + charge.name);
      },
      icon: <MdDelete />,
    },
  ];
}

export const ChargeListingPage = (props: ChargeListingPageProp) => {
  const [charges, setCharges] = useState<Charge[]>([]);
  const [charge, setCharge] = useState<Charge>();

  const getCharges = async (name: string = "") => {
    const res = await chargeService.getCharges({
      name: name,
    });

    if (res.status) {
      setCharges(res.data ?? []);
    }
  };

  return (
    <div className="row">
      <div className="col">
        <Frame>
          <h4>Charges</h4>
          <p className="text-secondary">Displays the list of charges</p>
          <GridView
            keys={["name"]}
            name={"lstCharges"}
            rowsPerPage={50}
            showSearchBar={true}
            showAddButton={true}
            onAddRecord={() => setCharge({})}
          >
            <GridView.Header>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Charge Type</th>
                  <th>Action</th>
                </tr>
              </thead>
            </GridView.Header>
            <GridView.Content>
              {charges.map((d) => (
                <tr>
                  <td>{d.name}</td>
                  <td>{d.description}</td>
                  <td>{d.value}</td>
                  <td>{d.usePercentage ? "Percentage" : "Flat charge"}</td>
                  <td>
                    <ContextMenu menu={getChargesContextMenu()} item={d} />
                  </td>
                </tr>
              ))}
            </GridView.Content>
          </GridView>

          {charge && (
            <FormModal onClose={() => setCharge(undefined)}>
              <ChargeSetupForm
                onChargeSaved={(charge) => {
                  setCharges([...charges, charge]);
                }}
              />
            </FormModal>
          )}
        </Frame>
      </div>
    </div>
  );
};

export type ChargeSetupFormProps = {
  onChargeSaved?: (charge: Charge) => void;
  charge?: Charge;
};

export function ChargeSetupForm(props: ChargeSetupFormProps) {
  const [charge, setCharge] = useState<Charge | undefined>(props?.charge);

  const accountCacheService = new AccountCacheService();

  return (
    <div className="row">
      <div className="col">
        <h3>Create Charge</h3>
        <div className="form-group">
          <label htmlFor="chargeName" className="control-label">
            Name of charge
          </label>
          <input
            type="text"
            name="chargeName"
            id="chargeName"
            placeholder="Enter charge name"
            className="form-control"
            value={charge?.name}
            onChange={(e) =>
              setCharge({ ...charge, name: e.currentTarget.value })
            }
          />
        </div>
        <div className="row">
          <div className="col">
            <div className="form-group">
              <label htmlFor="chargeValue" className="control-label">
                Value
              </label>
              <input
                type="number"
                name="chargeValue"
                id="chargeValue"
                placeholder="Enter value"
                className="form-control"
                value={charge?.name}
                onChange={(e) =>
                  setCharge({ ...charge, value: Number(e.currentTarget.value) })
                }
              />
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <label htmlFor="transactionType" className="control-label">
                Transaction Type
              </label>
              <select
                name="transactionType"
                id="transactionType"
                title="Select TransactionType"
                className="form-control"
                value={charge?.transactionType}
                onChange={(e) =>
                  setCharge({
                    ...charge,
                    transactionType: e.currentTarget.value,
                  })
                }
              >
                <option value={""}>Select Transaction Type</option>
                {accountCacheService.getTransationTypes().map((d) => {
                  return <option value={d.id}>{d.name}</option>;
                })}
              </select>
            </div>
          </div>
        </div>

        <FindAccount
          text="Income Account"
          id="incomeAccountId"
          onAccountSelected={(account) => {
            charge!.incomeAccountId = account.id;
          }}
        />

        <div className="row">
          <div className="col">
            <div className="form-group" style={{ marginTop: "10px" }}>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={charge?.usePercentage}
                  onChange={(e) =>
                    setCharge({
                      ...charge,
                      usePercentage: e.currentTarget.checked,
                    })
                  }
                  id="usePercentage"
                />
                <label className="form-check-label" htmlFor="usePercentage">
                  Use Percentage
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={charge?.usePercentage}
                  onChange={(e) =>
                    setCharge({
                      ...charge,
                      usePercentage: e.currentTarget.checked,
                    })
                  }
                  name="chargePerTransaction"
                  id="chargePerTransaction"
                />
                <label
                  className="form-check-label"
                  htmlFor="chargePerTransaction"
                >
                  Charge Per Transaction
                </label>
              </div>
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <label htmlFor="direction" className="control-label">
                Source (Dest. Acct / Source Acct.)
              </label>
              <select
                className="form-control"
                name="direction"
                id="direction"
                value={charge?.chargeDirection}
                onChange={(e) =>
                  setCharge({
                    ...charge,
                    chargeDirection: Number(e.currentTarget.value),
                  })
                }
              >
                <option>Select Charge Account</option>
                <option value={1} selected>
                  Source Account
                </option>
                <option value={2}>Destination Account</option>
              </select>
            </div>
          </div>
        </div>

        <FormCommand
          onSubmit={async () => {
            const res = await chargeService.addOrCreateCharge(charge!);
            if (res.status) {
              props?.onChargeSaved?.(charge!);
            }
          }}
        />
      </div>
    </div>
  );
}
